// src/encounter/ap-dialog-label-style.tsx
// ONE field-label treatment for every A&P order dialog (PROD-1801, Andrew 2 Aug: "the numeric picker
// title is upper case and the normal title is pascal, I want all to be the same").
//
// TARGET = UPPERCASE, and the reason matters: Flutter's live dialog gives EVERY field label the same
// 12px bold uppercase treatment. The DS `NumberSelector` already matches that exactly; so do our own
// hand-rolled labels (the MEDICATIONS header, Frequency, On which days, the substitution switch). The
// controls that were OUT of step are the other DS-labelled ones — TextField / TextArea / Select /
// DatePicker — which render 13-14px medium sentence-case. So this is not "make the counters match the
// fields", it is the reverse: bring the four drifting controls onto the treatment the counters and
// Flutter already share.
//
// WHY A SCOPE CLASS AND NOT A PROP: none of these components expose a label-style prop; the treatment
// is baked into their internals. The scoped rule that reaches into them was graduated into the Signal
// DS (round 4d, `src/styles/field-labels.css`) as the `field-labels-caps` class — this module now only
// names it, so the PA injects no <style> of its own.
//
// THE PART THAT IS EASY TO GET WRONG — the DS has THREE label shapes, not two:
//   • NumberSelector      → text sits on the <label> itself
//   • TextField/TextArea  → text sits on the <label> itself
//   • Select/DatePicker   → <label> WRAPS an inner <span class="text-sm font-medium …"> (FieldShell)
// A plain `label { … }` rule therefore silently misses Select and DatePicker. The med dialog's
// pre-existing label-colour rule had exactly that hole and only LOOKED correct because FieldShell's
// span already used --color-text-secondary by coincidence. Hence the DS rule's `label > span` arm, which
// carries the size/weight too; it also skips selection-control labels (a diagnosis checkbox's label is
// clinical content, not a caption) and the required asterisk.

/** Scope class — put it on the dialog's outermost element. The rule is the Signal DS `field-labels-caps`. */
export const AP_DIALOG_LABELS = 'field-labels-caps';

/**
 * THE canonical field-label treatment as a class string — for the labels we render ourselves (the
 * MEDICATIONS header, Frequency, On which days, the substitution switch, the shell's field label, the
 * referral's "Refer to"): 12px bold uppercase, 0.01em tracking, hint colour — the same values the DS
 * `field-labels-caps` rule gives the DS-rendered labels, through the same tokens.
 */
export const AP_FIELD_LABEL_CLASS =
  'block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint';
